import { useState } from "react"
import { useDispatch } from "react-redux"

import { ConfigContainer } from "components"
import { Title, Container, Text, Select } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import { members } from "state_management"
import useT from "utils/hooks/useT"

// Mirrors MemberConfig's language list; the codes the backend accepts on the member payload.
const PLANNERTECH_LANGUAGE_OPTIONS = [
  { id: "cs", name: "Čeština" },
  { id: "da", name: "Dansk" },
  { id: "de", name: "Deutsch" },
  { id: "el", name: "Ελληνικά" },
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" },
  { id: "it", name: "Italiano" },
  { id: "ko", name: "한국어" },
  { id: "pl", name: "Polski" },
  { id: "pt", name: "Português" },
]

// "Add QA member" (SPEC-058 A): the QA Manager's create form. Deliberately a QA-scoped form
// rather than MemberConfig with everything locked: the planning role is FIXED to Member (the
// server refuses anything else from a qa-manager — SPEC-057 L1, the columns never cross) and
// there is no password (the new member gets a welcome mail and picks their own — SPEC-057 L2).
//
// SPEC-061 (#4153) changed two things here. This is now the ONLY moment the `qa` role can be
// established — it cannot be granted to an existing member any more — so the submit goes to the
// dedicated `POST /lims/qa-members` and carries the QA Manager's ELECTRONIC SIGNATURE. Appointing
// someone to QA was already a signed act; moving it to creation time unsigned would have dropped
// that attestation as a side effect.
//
// The planning details are no longer sent at all. Work hours, work week and the MEMBER role come
// from the server's column defaults (which already matched what this form used to state), and
// plan visibility is set FALSE server-side (#4154) — a default the planner may turn on later in
// Setup › Team if the person also performs tasks. One place for each of them to change.
export default function QaMemberConfig({ onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [initials, setInitials] = useState("")
  const [email, setEmail] = useState("")
  const [plannertechLanguage, setPlannertechLanguage] = useState("")
  // The form collects the details, then the e-signature dialog collects the QA Manager's
  // re-authentication; the creation happens on signing, so an abandoned signature creates nothing.
  const [signing, setSigning] = useState(false)

  const submit = () => {
    setSigning(true)
  }

  // The promise is returned so the dialog stays open with an inline error on a 403 and closes on
  // success — the same contract every other signing dialog in LIMS uses.
  const signAndCreate = (signature) => {
    const payload = { initials, email }
    if (plannertechLanguage) {
      payload.plannertechLanguage = plannertechLanguage
    }
    return dispatch(members.createQaMember(payload, signature)).then(onClose)
  }

  if (signing) {
    return (
      <ESignatureModal
        title={t("limsQaTeamAddMember")}
        onSign={signAndCreate}
        onCancel={() => setSigning(false)}
      />
    )
  }

  return (
    <ConfigContainer title={t("limsQaTeamAddMember")} submitHandler={submit} cancelHandler={onClose}>
      <Container>
        <Title>{t("initials")}</Title>
        <Text required value={initials} textTransform={"uppercase"} onChange={setInitials} />
      </Container>
      <Container>
        <Title>{t("emailLabel")}</Title>
        <Text
          required
          type="email"
          value={email}
          textTransform={"lowercase"}
          onValidate={(value) => (value.includes("..") ? t("emailCannotContainDoubleDots") : undefined)}
          onChange={setEmail}
        />
      </Container>
      <Container>
        <Title>{t("plannertechLanguage")}</Title>
        <Select
          isMulti={false}
          collection={PLANNERTECH_LANGUAGE_OPTIONS}
          value={plannertechLanguage}
          onChange={(values) => setPlannertechLanguage(values[0] || "")}
          includeNone
          noneLabel={t("default")}
        />
      </Container>
      {/* #4160 (supersedes #4152): there was a "Role" field here that no-one could edit — a label
          over three sentences of prose about a planning role this form fixes to Member anyway. A
          field the user cannot act on is not a field, so it is gone. The facts it carried (the QA
          role is set at creation and cannot be changed afterwards; a QA-only member starts hidden
          on the plan) live in the in-app HELP under LIMS › QA team, which is where a reader looks
          for the rule rather than re-reading it on every create. */}
    </ConfigContainer>
  )
}
